import type { LearningDomain, Subject } from '@/content/schema';
import { bank, lesson, m, objective, opts, SAMPLE, type ObjectiveInput } from '../helpers';

export const subject: Subject = {
  id: 'french',
  name: { ar: 'الفرنسيّة', fr: 'Français', en: 'French' },
  glyph: 'Fr',
  color: 'french',
  teachingLanguage: 'fr',
  levels: ['y2', 'y3', 'y4', 'y5', 'y6'],
  availabilityNote: {
    ar: 'صفحة البرامج الرسميّة تُدرج الفرنسيّة بداية من الدرجة الثانية، وأُعلن في 2019 عن تدريسها من السنة الثانية. يجب التثبّت من ذلك.',
    fr: 'La page des programmes officiels place le français dès le 2e degré ; une annonce de 2019 l’a avancé en 2e année. À vérifier.',
    en: 'The official programmes page lists French from the second degree; a 2019 announcement moved it to year 2. To be verified.',
  },
  sources: [{ sourceId: 'tn-moe-programmes' }, { sourceId: 'tn-2019-languages-announcement' }],
  ...SAMPLE,
};

export const domains: LearningDomain[] = [
  {
    id: 'fr.vocabulary',
    subjectId: 'french',
    name: { ar: 'المفردات', fr: 'Vocabulaire', en: 'Vocabulary' },
  },
  {
    id: 'fr.phonics',
    subjectId: 'french',
    name: { ar: 'الأصوات والحروف', fr: 'Sons et lettres', en: 'Sounds and letters' },
  },
  {
    id: 'fr.grammar',
    subjectId: 'french',
    name: { ar: 'القواعد', fr: 'Grammaire et conjugaison', en: 'Grammar' },
  },
  { id: 'fr.reading', subjectId: 'french', name: { ar: 'القراءة', fr: 'Lecture', en: 'Reading' } },
];

const o = (input: ObjectiveInput) => objective('french', 'fr', input);

export const objectives = [
  o({
    id: 'fr.school-objects',
    domainId: 'fr.vocabulary',
    levels: ['y2', 'y3'],
    order: 10,
    difficulty: 1,
    title: { ar: 'أدوات القسم بالفرنسيّة', fr: 'Les objets de la classe', en: 'Classroom objects' },
    description: {
      ar: 'يسمّي أدوات القسم بالفرنسيّة.',
      fr: 'Nommer les objets de la classe.',
      en: 'Name classroom objects in French.',
    },
    games: ['quick-quiz', 'find-correct', 'memory', 'true-false'],
  }),
  o({
    id: 'fr.sounds',
    domainId: 'fr.phonics',
    levels: ['y2', 'y3'],
    order: 20,
    difficulty: 1,
    title: { ar: 'الأصوات والحروف', fr: 'Les sons et les lettres', en: 'Sounds and letters' },
    description: {
      ar: 'يتعرّف على الصوت الأوّل في الكلمة الفرنسيّة.',
      fr: 'Reconnaître le premier son d’un mot.',
      en: 'Recognise the first sound of a French word.',
    },
    games: ['letter-sound', 'quick-quiz', 'find-correct', 'memory', 'true-false'],
  }),
  o({
    id: 'fr.animals',
    domainId: 'fr.vocabulary',
    levels: ['y3'],
    order: 30,
    difficulty: 2,
    prerequisites: ['fr.school-objects'],
    title: { ar: 'الحيوانات بالفرنسيّة', fr: 'Les animaux', en: 'Animals' },
    description: {
      ar: 'يسمّي الحيوانات بالفرنسيّة مع أداة التعريف.',
      fr: 'Nommer des animaux avec leur article.',
      en: 'Name animals with their article.',
    },
    games: ['quick-quiz', 'find-correct', 'memory', 'true-false'],
  }),
  o({
    id: 'fr.articles',
    domainId: 'fr.grammar',
    levels: ['y3', 'y4'],
    order: 40,
    difficulty: 2,
    prerequisites: ['fr.animals'],
    title: { ar: 'أدوات التعريف والتنكير', fr: 'Les articles', en: 'Articles' },
    description: {
      ar: 'يختار الأداة المناسبة: le, la, les, l’, un, une, des.',
      fr: 'Choisir le bon article.',
      en: 'Choose the right article.',
    },
    games: ['quick-quiz', 'find-correct', 'true-false'],
  }),
  o({
    id: 'fr.word-building',
    domainId: 'fr.phonics',
    levels: ['y3'],
    order: 50,
    difficulty: 2,
    prerequisites: ['fr.sounds'],
    title: { ar: 'أكتب كلمة فرنسيّة', fr: 'J’écris un mot', en: 'Spelling French words' },
    description: {
      ar: 'يرتّب الحروف ليكتب كلمة فرنسيّة.',
      fr: 'Remettre les lettres dans l’ordre.',
      en: 'Put letters in order to spell a word.',
    },
    games: ['word-builder', 'quick-quiz'],
  }),
  o({
    id: 'fr.sentences',
    domainId: 'fr.grammar',
    levels: ['y4'],
    order: 60,
    difficulty: 3,
    prerequisites: ['fr.articles'],
    title: {
      ar: 'أركّب جملة فرنسيّة',
      fr: 'Je construis une phrase',
      en: 'Building French sentences',
    },
    description: {
      ar: 'يرتّب الكلمات ليكوّن جملة فرنسيّة.',
      fr: 'Ordonner des mots pour former une phrase.',
      en: 'Put words in order to make a sentence.',
    },
    games: ['sentence-builder', 'quick-quiz'],
  }),
  o({
    id: 'fr.etre-avoir',
    domainId: 'fr.grammar',
    levels: ['y4', 'y5'],
    order: 70,
    difficulty: 3,
    prerequisites: ['fr.articles'],
    title: {
      ar: 'تصريف être وavoir',
      fr: 'Les verbes être et avoir',
      en: 'The verbs être and avoir',
    },
    description: {
      ar: 'يصرّف الفعلين être وavoir في الحاضر.',
      fr: 'Conjuguer être et avoir au présent.',
      en: 'Conjugate être and avoir in the present.',
    },
    games: ['quick-quiz', 'find-correct', 'true-false'],
  }),
  o({
    id: 'fr.plural',
    domainId: 'fr.grammar',
    levels: ['y5'],
    order: 80,
    difficulty: 3,
    prerequisites: ['fr.articles'],
    title: {
      ar: 'المفرد والجمع بالفرنسيّة',
      fr: 'Le singulier et le pluriel',
      en: 'Singular and plural',
    },
    description: {
      ar: 'يميّز المفرد من الجمع ويكوّن الجمع.',
      fr: 'Distinguer singulier et pluriel.',
      en: 'Tell singular from plural.',
    },
    games: ['categorize', 'quick-quiz', 'find-correct', 'true-false'],
  }),
  o({
    id: 'fr.reading',
    domainId: 'fr.reading',
    levels: ['y6'],
    order: 90,
    difficulty: 3,
    prerequisites: ['fr.sentences'],
    title: { ar: 'أقرأ نصًّا فرنسيًّا', fr: 'Je lis et je comprends', en: 'Reading in French' },
    description: {
      ar: 'يقرأ نصًّا قصيرًا بالفرنسيّة ويجيب عن أسئلة.',
      fr: 'Lire un court texte et répondre.',
      en: 'Read a short text and answer questions.',
    },
    games: ['quick-quiz', 'find-correct', 'true-false'],
  }),
];

const TEXT =
  'Ce matin, Youssef va au marché avec son père. Ils achètent des tomates, des oranges et du pain. Sur le chemin du retour, Youssef voit un petit chat. Il lui donne un peu d’eau. Le chat est content et suit Youssef jusqu’à la maison.';

export const items = [
  ...bank('fr.school-objects', 1, [
    {
      kind: 'choice',
      prompt: 'Comment s’appelle cet objet ?',
      visual: { emoji: '✏️', label: 'crayon' },
      ...opts('un crayon', m('une règle', 'vocabulary'), m('un cahier', 'vocabulary')),
    },
    {
      kind: 'choice',
      prompt: 'Comment s’appelle cet objet ?',
      visual: { emoji: '📕', label: 'livre' },
      ...opts('un livre', m('un cartable', 'vocabulary'), m('une trousse', 'vocabulary')),
    },
    {
      kind: 'choice',
      prompt: 'Comment s’appelle cet objet ?',
      visual: { emoji: '🎒', label: 'cartable' },
      ...opts('un cartable', m('une table', 'vocabulary'), m('un stylo', 'vocabulary')),
    },
    {
      kind: 'choice',
      prompt: 'Comment s’appellent ces objets ?',
      visual: { emoji: '✂️', label: 'ciseaux' },
      ...opts('des ciseaux', m('une gomme', 'vocabulary'), m('un crayon', 'vocabulary')),
    },
    {
      kind: 'choice',
      prompt: 'Comment s’appelle cet objet ?',
      visual: { emoji: '📏', label: 'règle' },
      ...opts('une règle', m('une craie', 'vocabulary'), m('un livre', 'vocabulary')),
    },
    {
      kind: 'trueFalse',
      statement: 'C’est une règle.',
      visual: { emoji: '📏', label: 'règle' },
      answer: true,
    },
    {
      kind: 'trueFalse',
      statement: 'C’est une trousse.',
      visual: { emoji: '🎒', label: 'cartable' },
      answer: false,
      mistake: 'vocabulary',
      explanation: 'C’est un cartable.',
    },
    {
      kind: 'match',
      prompt: 'Relie chaque image à son nom.',
      pairs: [
        { left: '✏️', right: 'crayon' },
        { left: '📕', right: 'livre' },
        { left: '🎒', right: 'cartable' },
        { left: '✂️', right: 'ciseaux' },
        { left: '📏', right: 'règle' },
        { left: '🖊️', right: 'stylo' },
      ],
      mistake: 'vocabulary',
    },
  ]),
  ...bank('fr.sounds', 1, [
    {
      kind: 'choice',
      prompt: 'Quel mot commence par le son [m] ?',
      audio: { text: 'mmm' },
      ...opts(
        { text: 'maison', emoji: '🏠' },
        m('nid', 'similar-sound'),
        m('lune', 'unknown', '🌙'),
      ),
    },
    {
      kind: 'choice',
      prompt: 'Quel mot commence par le son [s] ?',
      ...opts(
        { text: 'soleil', emoji: '☀️' },
        m('chat', 'similar-sound', '🐱'),
        m('zèbre', 'similar-sound', '🦓'),
      ),
    },
    {
      kind: 'choice',
      prompt: 'Quel mot commence par le son [p] ?',
      ...opts(
        { text: 'pomme', emoji: '🍎' },
        m('banane', 'similar-sound', '🍌'),
        m('tomate', 'similar-sound', '🍅'),
      ),
    },
    {
      kind: 'choice',
      prompt: 'Quel mot commence par le son [l] ?',
      ...opts(
        { text: 'lion', emoji: '🦁' },
        m('rose', 'similar-sound', '🌹'),
        m('nez', 'similar-sound', '👃'),
      ),
    },
    {
      kind: 'choice',
      prompt: 'Quel mot commence par le son [r] ?',
      ...opts(
        { text: 'robe', emoji: '👗' },
        m('lait', 'similar-sound', '🥛'),
        m('gâteau', 'similar-sound', '🎂'),
      ),
    },
    {
      kind: 'choice',
      prompt: 'Quel mot commence par le son [b] ?',
      ...opts(
        { text: 'bateau', emoji: '⛵' },
        m('poisson', 'similar-sound', '🐟'),
        m('dé', 'similar-sound', '🎲'),
      ),
    },
    { kind: 'trueFalse', statement: 'Le mot « lune » commence par le son [l].', answer: true },
    {
      kind: 'trueFalse',
      statement: 'Le mot « chat » commence par le son [s].',
      answer: false,
      mistake: 'similar-sound',
      explanation: '« chat » commence par le son [ch].',
    },
    {
      kind: 'match',
      prompt: 'Relie chaque lettre à un mot qui commence par elle.',
      pairs: [
        { left: 'm', right: 'maison', emoji: '🏠' },
        { left: 's', right: 'soleil', emoji: '☀️' },
        { left: 'p', right: 'pomme', emoji: '🍎' },
        { left: 'l', right: 'lion', emoji: '🦁' },
        { left: 'r', right: 'robe', emoji: '👗' },
        { left: 'b', right: 'bateau', emoji: '⛵' },
      ],
      mistake: 'similar-sound',
    },
  ]),
  ...bank('fr.animals', 2, [
    {
      kind: 'choice',
      prompt: 'Quel est cet animal ?',
      visual: { emoji: '🐶', label: 'chien' },
      ...opts('le chien', m('le chat', 'vocabulary'), m('le cheval', 'vocabulary')),
    },
    {
      kind: 'choice',
      prompt: 'Quel est cet animal ?',
      visual: { emoji: '🐄', label: 'vache' },
      ...opts('la vache', m('la poule', 'vocabulary'), m('le mouton', 'vocabulary')),
    },
    {
      kind: 'choice',
      prompt: 'Quel est cet animal ?',
      visual: { emoji: '🐔', label: 'poule' },
      ...opts('la poule', m('l’oiseau', 'vocabulary'), m('la vache', 'vocabulary')),
    },
    {
      kind: 'choice',
      prompt: 'Quel est cet animal ?',
      visual: { emoji: '🐴', label: 'cheval' },
      ...opts('le cheval', m('le chien', 'vocabulary'), m('le mouton', 'vocabulary')),
    },
    {
      kind: 'choice',
      prompt: 'Quel est cet animal ?',
      visual: { emoji: '🐟', label: 'poisson' },
      ...opts('le poisson', m('l’oiseau', 'vocabulary'), m('le chat', 'vocabulary')),
    },
    {
      kind: 'trueFalse',
      statement: 'C’est un mouton.',
      visual: { emoji: '🐑', label: 'mouton' },
      answer: true,
    },
    {
      kind: 'trueFalse',
      statement: 'C’est un oiseau.',
      visual: { emoji: '🐱', label: 'chat' },
      answer: false,
      mistake: 'vocabulary',
      explanation: 'C’est un chat.',
    },
    {
      kind: 'match',
      prompt: 'Relie chaque animal à son nom.',
      pairs: [
        { left: '🐶', right: 'le chien' },
        { left: '🐱', right: 'le chat' },
        { left: '🐄', right: 'la vache' },
        { left: '🐔', right: 'la poule' },
        { left: '🐑', right: 'le mouton' },
        { left: '🐦', right: 'l’oiseau' },
      ],
      mistake: 'vocabulary',
    },
  ]),
  ...bank('fr.articles', 2, [
    {
      kind: 'choice',
      prompt: 'Choisis le bon article : ___ pomme',
      ...opts('la', m('le', 'article'), m('les', 'agreement')),
    },
    {
      kind: 'choice',
      prompt: 'Choisis le bon article : ___ soleil',
      ...opts('le', m('la', 'article'), m('les', 'agreement')),
    },
    {
      kind: 'choice',
      prompt: 'Choisis le bon article : ___ fleurs',
      ...opts('les', m('la', 'agreement'), m('le', 'agreement')),
    },
    {
      kind: 'choice',
      prompt: 'Choisis le bon article : ___ école',
      ...opts('l’', m('la', 'article'), m('le', 'article')),
      hint: 'Devant une voyelle, « le » et « la » deviennent « l’ ».',
    },
    {
      kind: 'choice',
      prompt: 'Choisis le bon article : ___ arbre',
      ...opts('un', m('une', 'article'), m('des', 'agreement')),
    },
    {
      kind: 'choice',
      prompt: 'Choisis le bon article : ___ maison',
      ...opts('une', m('un', 'article'), m('des', 'agreement')),
    },
    {
      kind: 'choice',
      prompt: 'Choisis le bon article : ___ enfants',
      ...opts('des', m('un', 'agreement'), m('une', 'agreement')),
    },
    {
      kind: 'trueFalse',
      statement: 'On dit « la soleil ».',
      answer: false,
      mistake: 'article',
      explanation: 'On dit « le soleil ».',
    },
    { kind: 'trueFalse', statement: 'On dit « l’ami ».', answer: true },
  ]),
  ...bank('fr.word-building', 2, [
    {
      kind: 'order',
      prompt: 'Remets les lettres dans l’ordre :',
      unit: 'letter',
      visual: { emoji: '🐱', label: 'chat' },
      tokens: ['c', 'h', 'a', 't'],
      mistake: 'letter-order',
    },
    {
      kind: 'order',
      prompt: 'Remets les lettres dans l’ordre :',
      unit: 'letter',
      visual: { emoji: '🌙', label: 'lune' },
      tokens: ['l', 'u', 'n', 'e'],
      mistake: 'letter-order',
    },
    {
      kind: 'order',
      prompt: 'Remets les lettres dans l’ordre :',
      unit: 'letter',
      visual: { emoji: '🚲', label: 'vélo' },
      tokens: ['v', 'é', 'l', 'o'],
      mistake: 'letter-order',
    },
    {
      kind: 'order',
      prompt: 'Remets les lettres dans l’ordre :',
      unit: 'letter',
      visual: { emoji: '🍎', label: 'pomme' },
      tokens: ['p', 'o', 'm', 'm', 'e'],
      mistake: 'letter-order',
    },
    {
      kind: 'order',
      prompt: 'Remets les lettres dans l’ordre :',
      unit: 'letter',
      visual: { emoji: '📕', label: 'livre' },
      tokens: ['l', 'i', 'v', 'r', 'e'],
      mistake: 'letter-order',
    },
    {
      kind: 'order',
      prompt: 'Remets les lettres dans l’ordre :',
      unit: 'letter',
      visual: { emoji: '🌹', label: 'rose' },
      tokens: ['r', 'o', 's', 'e'],
      mistake: 'letter-order',
    },
  ]),
  ...bank('fr.sentences', 3, [
    {
      kind: 'order',
      prompt: 'Remets les mots dans l’ordre :',
      unit: 'word',
      tokens: ['Le', 'chat', 'dort', 'sur', 'le', 'lit.'],
      mistake: 'word-order',
    },
    {
      kind: 'order',
      prompt: 'Remets les mots dans l’ordre :',
      unit: 'word',
      tokens: ['Sami', 'mange', 'une', 'pomme', 'rouge.'],
      mistake: 'word-order',
    },
    {
      kind: 'order',
      prompt: 'Remets les mots dans l’ordre :',
      unit: 'word',
      tokens: ['Les', 'enfants', 'jouent', 'dans', 'la', 'cour.'],
      mistake: 'word-order',
    },
    {
      kind: 'order',
      prompt: 'Remets les mots dans l’ordre :',
      unit: 'word',
      tokens: ['Mariem', 'lit', 'un', 'beau', 'livre.'],
      mistake: 'word-order',
    },
    {
      kind: 'order',
      prompt: 'Remets les mots dans l’ordre :',
      unit: 'word',
      tokens: ['Nous', 'allons', 'à', 'l’école.'],
      mistake: 'word-order',
    },
  ]),
  ...bank('fr.etre-avoir', 3, [
    {
      kind: 'choice',
      prompt: 'Je ___ content.',
      ...opts('suis', m('es', 'conjugation'), m('est', 'conjugation')),
    },
    {
      kind: 'choice',
      prompt: 'Tu ___ un vélo.',
      ...opts('as', m('a', 'conjugation'), m('ai', 'conjugation')),
    },
    {
      kind: 'choice',
      prompt: 'Nous ___ en classe.',
      ...opts('sommes', m('êtes', 'conjugation'), m('sont', 'conjugation')),
    },
    {
      kind: 'choice',
      prompt: 'Ils ___ deux chats.',
      ...opts('ont', m('sont', 'conjugation'), m('avons', 'conjugation')),
    },
    {
      kind: 'choice',
      prompt: 'Elle ___ grande.',
      ...opts('est', m('a', 'conjugation'), m('es', 'conjugation')),
    },
    {
      kind: 'choice',
      prompt: 'Vous ___ un livre.',
      ...opts('avez', m('êtes', 'conjugation'), m('ont', 'conjugation')),
    },
    { kind: 'trueFalse', statement: 'On dit « nous avons ».', answer: true },
    {
      kind: 'trueFalse',
      statement: 'On dit « ils est ».',
      answer: false,
      mistake: 'conjugation',
      explanation: 'On dit « ils sont ».',
    },
  ]),
  ...bank('fr.plural', 3, [
    {
      kind: 'categorize',
      prompt: 'Range les mots :',
      categories: [
        { id: 'singular', label: 'singulier' },
        { id: 'plural', label: 'pluriel' },
      ],
      entries: [
        { text: 'le chat', category: 'singular' },
        { text: 'les chats', category: 'plural' },
        { text: 'une fleur', category: 'singular' },
        { text: 'des fleurs', category: 'plural' },
        { text: 'l’arbre', category: 'singular' },
        { text: 'les livres', category: 'plural' },
        { text: 'des oiseaux', category: 'plural' },
      ],
      mistake: 'agreement',
    },
    {
      kind: 'choice',
      prompt: 'Le pluriel de « un cheval » :',
      ...opts('des chevaux', m('des chevals', 'agreement'), m('un chevaux', 'agreement')),
    },
    {
      kind: 'choice',
      prompt: 'Le pluriel de « le jeu » :',
      ...opts('les jeux', m('les jeus', 'agreement'), m('le jeux', 'agreement')),
    },
    {
      kind: 'choice',
      prompt: 'Le pluriel de « une fleur » :',
      ...opts('des fleurs', m('des fleur', 'agreement'), m('une fleurs', 'agreement')),
    },
    { kind: 'trueFalse', statement: '« les oiseaux » est au pluriel.', answer: true },
    {
      kind: 'trueFalse',
      statement: '« des livre » est bien écrit.',
      answer: false,
      mistake: 'agreement',
      explanation: 'On écrit « des livres » avec un s.',
    },
  ]),
  ...bank('fr.reading', 3, [
    {
      kind: 'choice',
      passage: TEXT,
      prompt: 'Avec qui Youssef va-t-il au marché ?',
      ...opts(
        'avec son père',
        m('avec sa mère', 'comprehension'),
        m('avec son ami', 'comprehension'),
      ),
    },
    {
      kind: 'choice',
      passage: TEXT,
      prompt: 'Qu’est-ce qu’ils achètent ?',
      ...opts(
        'des tomates, des oranges et du pain',
        m('des pommes et du lait', 'comprehension'),
        m('des livres', 'comprehension'),
      ),
    },
    {
      kind: 'choice',
      passage: TEXT,
      prompt: 'Que donne Youssef au chat ?',
      ...opts('de l’eau', m('du pain', 'comprehension'), m('du lait', 'comprehension')),
    },
    {
      kind: 'trueFalse',
      passage: TEXT,
      statement: 'Le chat suit Youssef jusqu’à la maison.',
      answer: true,
    },
    {
      kind: 'trueFalse',
      passage: TEXT,
      statement: 'Youssef va au marché le soir.',
      answer: false,
      mistake: 'comprehension',
      explanation: 'Le texte dit : « Ce matin ».',
    },
  ]),
];

export const lessons = [
  lesson('fr.school-objects', 'Les objets de la classe', [
    { type: 'intro', text: 'Dans la classe, on utilise beaucoup d’objets. Apprenons leurs noms.' },
    {
      type: 'example',
      text: 'un crayon, un livre, une règle, un cartable',
      visual: { emoji: '✏️ 📕 📏 🎒', label: 'objets' },
    },
    { type: 'tip', text: 'Apprends le mot avec son article : un crayon, une règle.' },
  ]),
  lesson('fr.sounds', 'J’écoute le premier son', [
    { type: 'intro', text: 'Chaque mot commence par un son. Dis le mot lentement.' },
    {
      type: 'example',
      text: 'mmm… maison : le mot commence par le son [m].',
      visual: { emoji: '🏠', label: 'maison' },
    },
    { type: 'tip', text: 'Certains sons se ressemblent, comme [p] et [b]. Écoute bien.' },
  ]),
  lesson('fr.animals', 'Les animaux', [
    { type: 'intro', text: 'Apprenons le nom des animaux de la ferme et de la maison.' },
    {
      type: 'example',
      text: 'le chien, la vache, la poule, le mouton',
      visual: { emoji: '🐶 🐄 🐔 🐑', label: 'animaux' },
    },
    { type: 'tip', text: 'Retiens si le mot va avec « le » ou avec « la ».' },
  ]),
  lesson('fr.articles', 'Le, la, les, un, une, des', [
    {
      type: 'intro',
      text: '« le » et « un » vont avec les mots masculins, « la » et « une » avec les mots féminins.',
    },
    { type: 'example', text: 'le soleil, la pomme, les fleurs, l’école' },
    {
      type: 'rule',
      title: 'Masculin, féminin ou pluriel ?',
      text: '« le » et « un » vont avec un mot masculin, « la » et « une » avec un mot féminin, « les » et « des » avec le pluriel. Devant une voyelle, « le » et « la » deviennent « l’ ».',
      figure: {
        type: 'cards',
        cards: [
          { emoji: '☀️', title: 'le soleil', text: 'masculin' },
          { emoji: '🍎', title: 'la pomme', text: 'féminin' },
          { emoji: '🌼🌼', title: 'les fleurs', text: 'pluriel' },
        ],
      },
      examples: [
        { expression: 'le chat — un chat', text: 'masculin' },
        { expression: 'la table — une table', text: 'féminin' },
        { expression: 'les livres — des livres', text: 'pluriel' },
        { expression: 'l’école — l’arbre', text: 'devant une voyelle' },
      ],
    },
  ]),
  lesson('fr.word-building', 'J’écris un mot', [
    { type: 'intro', text: 'En français, on écrit de gauche à droite.' },
    { type: 'example', text: 'c + h + a + t = chat', visual: { emoji: '🐱', label: 'chat' } },
    { type: 'tip', text: 'Dis le mot lentement et écoute chaque son.' },
  ]),
  lesson('fr.sentences', 'Je construis une phrase', [
    { type: 'intro', text: 'Une phrase commence par une majuscule et se termine par un point.' },
    { type: 'example', text: 'Le chat dort sur le lit.' },
    {
      type: 'rule',
      title: 'La phrase',
      text: 'Une phrase commence par une majuscule et se termine par un point : . ou ? ou !',
      examples: [
        { expression: 'Le chat dort.' },
        { expression: 'Où est mon cartable ?' },
        { expression: 'Quelle belle fleur !' },
      ],
    },
    { type: 'tip', text: 'Cherche le mot avec la majuscule : il est au début.' },
  ]),
  lesson('fr.etre-avoir', 'Être et avoir', [
    { type: 'intro', text: 'Ce sont deux verbes très utilisés. Apprends-les par cœur.' },
    {
      type: 'example',
      text: 'je suis, tu es, il est, nous sommes, vous êtes, ils sont — j’ai, tu as, il a, nous avons, vous avez, ils ont',
    },
    {
      type: 'rule',
      title: 'Être ou avoir ?',
      text: '« Être » dit comment on est ou où on est ; « avoir » dit ce qu’on a.',
      examples: [
        { expression: 'Je suis grand.' },
        { expression: 'Nous sommes en classe.' },
        { expression: 'J’ai un vélo.' },
        { expression: 'Ils ont un chien.' },
      ],
    },
    { type: 'tip', text: 'Regarde bien le pronom avant de choisir.' },
  ]),
  lesson('fr.plural', 'Un ou plusieurs ?', [
    { type: 'intro', text: 'Au pluriel, on ajoute souvent un « s » au nom.' },
    { type: 'example', text: 'un chat → des chats ; un jeu → des jeux ; un cheval → des chevaux' },
    {
      type: 'rule',
      title: 'Le pluriel des noms',
      text: 'En général, on ajoute « s ». Les noms en « -eau » et « -eu » prennent un « x ». Beaucoup de noms en « -al » deviennent « -aux ».',
      examples: [
        { expression: 'un chat → des chats' },
        { expression: 'un gâteau → des gâteaux' },
        { expression: 'un jeu → des jeux' },
        { expression: 'un cheval → des chevaux' },
      ],
    },
    { type: 'tip', text: 'L’article (les, des) t’aide à reconnaître le pluriel.' },
  ]),
  lesson('fr.reading', 'Je lis et je comprends', [
    { type: 'intro', text: 'Lis le texte une première fois pour comprendre l’histoire.' },
    { type: 'tip', text: 'Relis la phrase du texte qui contient la réponse.' },
  ]),
];
